import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, AlertCircle, CheckCircle, TrendingUp } from 'lucide-react';

/**
 * InstallationCalendar — two separate critical paths.
 *
 * PRESENTATION is driven by the CHECKLIST sheet, because that is where the
 * statuses that lead up to a presentation live (TO BE SELECTED, ORDER SAMPLES,
 * SAMPLES ARRIVED, READY FOR PRESENTATION). INSTALL is driven by the FF&E,
 * where ordering and delivery are tracked.
 *
 * This used to read the FF&E for both. Since no FF&E item ever carries a
 * checklist status, every FF&E item counted as "not ready for presentation" —
 * including items already INSTALLED — and the presentation progress bar was
 * permanently 0%.
 */

const PRESENTATION_DONE = 'READY FOR PRESENTATION';

// Statuses on the CHECKLIST, and how long each still needs before it could be
// presented. Anything unrecognised is treated as not started.
const PRESENTATION_DAYS = {
  '': 21, 'BLANK': 21, 'TO BE SELECTED': 21,
  'PICKED': 14, 'RESEARCHING': 14,
  'ORDER SAMPLES': 10,
  'SAMPLES ARRIVED': 7,
  'GET QUOTE': 5, 'WAITING ON QT': 5,
};

const INSTALL_READY = [
  'DELIVERED TO RECEIVER', 'DELIVERED TO JOB SITE', 'RECEIVED',
  'READY FOR INSTALL', 'INSTALLING', 'INSTALLED',
];

const INSTALL_DAYS = {
  '': 84, 'TO BE SELECTED': 84,
  'RESEARCHING': 70, 'PENDING APPROVAL': 70,
  'APPROVED': 63,
  'IN PRODUCTION': 35,
  'SHIPPED': 7, 'IN TRANSIT': 7,
  'OUT FOR DELIVERY': 2,
};

const DAY_MS = 1000 * 60 * 60 * 24;

const addDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d;
};

function flattenItems(project) {
  const out = [];
  (project?.rooms || []).forEach(room => {
    (room.categories || []).forEach(category => {
      (category.subcategories || []).forEach(sub => {
        (sub.items || []).forEach(item => {
          out.push({ ...item, room_name: room.name, category_name: category.name });
        });
      });
    });
  });
  return out;
}

/** Presentation readiness, from the CHECKLIST. */
function computePresentation(items) {
  const counts = { total: items.length, ready: 0, to_be_selected: 0, in_progress: 0 };
  const blockers = [];
  let latest = new Date();

  items.forEach(item => {
    const status = (item.status || '').toUpperCase();

    if (status === PRESENTATION_DONE) {
      counts.ready++;
      return;
    }
    if (['', 'BLANK', 'TO BE SELECTED'].includes(status)) counts.to_be_selected++;
    else counts.in_progress++;

    // "ASK <someone>" is a question outstanding, whoever it is addressed to.
    const days = status.includes('ASK') ? 3
      : (PRESENTATION_DAYS[status] !== undefined ? PRESENTATION_DAYS[status] : 21);

    const when = addDays(days);
    if (when > latest) latest = when;
    blockers.push({
      ...item,
      reason: `Status: ${item.status || 'Not started'}`,
      days_delayed: days,
      expected_arrival: when.toLocaleDateString(),
    });
  });

  return { date: latest, blockers, counts };
}

/** Delivery readiness, from the FF&E. */
function computeInstall(items) {
  const counts = {
    total: items.length, ordered: 0, in_transit: 0, delivered: 0,
    ready_for_install: 0, installed: 0, not_started: 0,
  };
  const blockers = [];
  let latest = new Date();

  items.forEach(item => {
    const status = (item.status || '').toUpperCase();

    if (status === 'INSTALLED') counts.installed++;
    else if (['READY FOR INSTALL', 'INSTALLING'].includes(status)) counts.ready_for_install++;
    else if (['DELIVERED TO RECEIVER', 'DELIVERED TO JOB SITE', 'RECEIVED'].includes(status)) counts.delivered++;
    else if (['SHIPPED', 'IN TRANSIT', 'OUT FOR DELIVERY'].includes(status)) counts.in_transit++;
    else if (['ORDERED', 'CONFIRMED', 'IN PRODUCTION', 'PICKED'].includes(status)) counts.ordered++;
    else counts.not_started++;

    if (INSTALL_READY.includes(status)) return;   // nothing left to wait for

    let readyDate;
    if (item.estimated_delivery_date) {
      readyDate = new Date(item.estimated_delivery_date);
    } else if (item.order_date) {
      const leadWeeks = parseInt(item.lead_time_weeks, 10) || 8;
      readyDate = new Date(item.order_date);
      readyDate.setDate(readyDate.getDate() + leadWeeks * 7);
    } else {
      let days;
      if (['ORDERED', 'PICKED', 'CONFIRMED'].includes(status)) {
        days = (parseInt(item.lead_time_weeks, 10) || 8) * 7;
      } else {
        days = PRESENTATION_DONE === status ? 63
          : (INSTALL_DAYS[status] !== undefined ? INSTALL_DAYS[status] : 84);
      }
      if (status === 'BACKORDERED' || item.stock_status === 'OUT OF STOCK') {
        days += parseInt(item.restock_days, 10) || 30;
      }
      readyDate = addDays(days);
    }

    if (isNaN(readyDate.getTime())) readyDate = addDays(84);   // unparseable date
    if (readyDate > latest) latest = readyDate;

    const daysUntil = Math.ceil((readyDate - new Date()) / DAY_MS);
    if (daysUntil > 30 || status === 'BACKORDERED' || item.stock_status === 'OUT OF STOCK') {
      blockers.push({
        ...item,
        reason: status === 'BACKORDERED' ? 'Backordered — waiting for restock'
          : item.stock_status === 'OUT OF STOCK' ? `Out of stock (restock: ${item.restock_date || 'TBD'})`
            : !item.order_date ? 'Not yet ordered'
              : `Long lead time (${Math.ceil(daysUntil / 7)} weeks)`,
        days_delayed: daysUntil,
        expected_arrival: readyDate.toLocaleDateString(),
      });
    }
  });

  return { date: latest, blockers, counts };
}

/** Worst blocker per item, most delayed first — one row per item. */
function rankBlockers(blockers) {
  const worst = new Map();
  blockers.forEach((b, i) => {
    const id = b.id || `blocker-${i}`;
    const existing = worst.get(id);
    if (!existing || (b.days_delayed || 0) > (existing.days_delayed || 0)) {
      worst.set(id, { ...b, id });
    }
  });
  return [...worst.values()].sort((a, b) => b.days_delayed - a.days_delayed).slice(0, 15);
}

const pct = (part, total) => (total > 0 ? Math.min(Math.round((part / total) * 100), 100) : 0);

const CriticalPathTable = ({ title, subtitle, source, items, headerBg, testId }) => (
  <div className="rounded-2xl border border-[#D4A574]/60 mb-8 overflow-hidden" style={{
    background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(30,30,30,0.9) 50%, rgba(0,0,0,0.95) 100%)',
  }} data-testid={testId}>
    <div className="p-6 border-b border-[#D4A574]/60 flex items-center gap-4"
      style={{ background: headerBg, boxShadow: '0 0 20px #00000040' }}>
      <AlertCircle className="w-8 h-8 text-white" />
      <div>
        <h3 className="text-2xl font-bold text-white">{title}</h3>
        <p className="text-sm text-white/80">{subtitle}</p>
      </div>
      <span className="ml-auto text-xs text-white/70 uppercase tracking-wide whitespace-nowrap">
        from the {source}
      </span>
    </div>

    {items.length === 0 ? (
      <div className="p-8 text-center">
        <CheckCircle className="w-16 h-16 text-[#D4A574] mx-auto mb-4" />
        <div className="text-xl text-[#D4A574] font-bold">All items on track 🎉</div>
        <div className="text-sm text-[#B49B7E] mt-2">Nothing is holding this up</div>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #EF4444FF 0%, #EF4444AA 50%, #EF4444FF 100%)' }}>
              <th className="px-6 py-4 text-left text-sm font-bold text-white">ITEM</th>
              <th className="px-6 py-4 text-left text-sm font-bold text-white">ROOM</th>
              <th className="px-6 py-4 text-left text-sm font-bold text-white">REASON</th>
              <th className="px-6 py-4 text-left text-sm font-bold text-white">DAYS</th>
              <th className="px-6 py-4 text-left text-sm font-bold text-white">EXPECTED</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={item.id} style={{
                background: index % 2 === 0
                  ? 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(30,30,30,0.9) 50%, rgba(0,0,0,0.95) 100%)'
                  : 'linear-gradient(135deg, rgba(15,15,25,0.95) 0%, rgba(45,45,55,0.9) 50%, rgba(15,15,25,0.95) 100%)',
              }}>
                <td className="px-6 py-4 text-[#D4A574] font-semibold border-b border-[#D4A574]/30">{item.name}</td>
                <td className="px-6 py-4 text-white border-b border-[#D4A574]/30">{item.room_name}</td>
                <td className="px-6 py-4 text-red-400 border-b border-[#D4A574]/30">{item.reason}</td>
                <td className="px-6 py-4 text-yellow-400 font-bold border-b border-[#D4A574]/30">{item.days_delayed}d</td>
                <td className="px-6 py-4 text-white border-b border-[#D4A574]/30">{item.expected_arrival || 'TBD'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const InstallationCalendar = ({ projectId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [presentation, setPresentation] = useState(null);
  const [install, setInstall] = useState(null);

  const load = useCallback(async () => {
    const BACKEND_URL = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);
    setLoading(true);
    try {
      // Two sheets, two timelines: the checklist drives the presentation, the
      // FF&E drives the install.
      const [clRes, ffeRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/projects/${projectId}?sheet_type=checklist`),
        fetch(`${BACKEND_URL}/api/projects/${projectId}?sheet_type=ffe`),
      ]);
      if (!clRes.ok || !ffeRes.ok) {
        throw new Error(`Could not load the project (checklist ${clRes.status}, FF&E ${ffeRes.status})`);
      }
      const [checklist, ffe] = await Promise.all([clRes.json(), ffeRes.json()]);
      setPresentation(computePresentation(flattenItems(checklist)));
      setInstall(computeInstall(flattenItems(ffe)));
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (projectId) load(); }, [projectId, load]);

  if (loading) {
    return <div className="text-center py-12 text-[#D4C5A9]">Loading installation timeline...</div>;
  }
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 mb-3">{error}</p>
        <button onClick={load} className="px-4 py-2 bg-[#D4A574] text-black rounded font-bold">Try again</button>
      </div>
    );
  }

  const pres = presentation || { date: new Date(), blockers: [], counts: { total: 0, ready: 0, to_be_selected: 0, in_progress: 0 } };
  const inst = install || { date: new Date(), blockers: [], counts: { total: 0 } };
  const pc = pres.counts;
  const ic = inst.counts;

  const today = new Date();
  const daysToPresentation = Math.max(0, Math.ceil((pres.date - today) / DAY_MS));
  const daysToInstall = Math.max(0, Math.ceil((inst.date - today) / DAY_MS));

  const presBlockers = rankBlockers(pres.blockers);
  const instBlockers = rankBlockers(inst.blockers);

  // Markers sit where the dates actually fall, rather than at fixed positions.
  const span = Math.max(daysToInstall, daysToPresentation, 1);
  const presLeft = Math.min(88, Math.round((daysToPresentation / span) * 88));
  const instLeft = Math.min(88, Math.round((daysToInstall / span) * 88));
  const installedPct = pct(ic.delivered + ic.ready_for_install + ic.installed, ic.total);
  const presPct = pct(pc.ready, pc.total);

  const card = 'rounded-2xl p-8 border border-[#D4A574]/60';
  const panel = 'rounded-2xl p-8 border border-[#D4A574]/60 mb-8';

  return (
    <div className="w-full" style={{ backgroundColor: '#0F172A', padding: '24px' }}>
      <h2 className="text-3xl font-bold text-[#D4A574] mb-6">📅 Installation Timeline</h2>

      {/* PREDICTED DATES */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className={card} style={{
          background: 'linear-gradient(135deg, #8B4513FF 0%, #8B4513AA 50%, #8B4513FF 100%)',
          boxShadow: '0 0 30px #8B451360, inset 0 0 50px rgba(255, 255, 255, 0.12)',
        }} data-testid="presentation-card">
          <div className="flex items-center gap-4 mb-4">
            <TrendingUp className="w-12 h-12 text-white" />
            <div>
              <h3 className="text-2xl font-bold text-white">Predicted Presentation Date</h3>
              <p className="text-sm text-white/80">From the Checklist — when every item is ready to show the client</p>
            </div>
          </div>
          <div className="text-4xl font-bold text-white mb-2">
            {pres.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
          <div className="text-lg text-white">
            {daysToPresentation > 0 ? `${daysToPresentation} days from now` : 'Ready now!'}
          </div>
          <div className="mt-4 text-sm text-white/80">
            {pc.total === 0
              ? 'No checklist items yet'
              : `${pc.to_be_selected + pc.in_progress} of ${pc.total} items not yet ready for presentation`}
          </div>
        </div>

        <div className={card} style={{
          background: 'linear-gradient(135deg, #4A3A6AFF 0%, #4A3A6AAA 50%, #4A3A6AFF 100%)',
          boxShadow: '0 0 30px #4A3A6A60, inset 0 0 50px rgba(255, 255, 255, 0.12)',
        }} data-testid="install-card">
          <div className="flex items-center gap-4 mb-4">
            <Calendar className="w-12 h-12 text-white" />
            <div>
              <h3 className="text-2xl font-bold text-white">Predicted Install Date</h3>
              <p className="text-sm text-white/80">From the FF&amp;E — when everything is delivered and ready to install</p>
            </div>
          </div>
          <div className="text-4xl font-bold text-white mb-2">
            {inst.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
          <div className="text-lg text-white">
            {daysToInstall > 0 ? `${daysToInstall} days from now` : 'Ready to install!'}
          </div>
          <div className="mt-4 text-sm text-white/80">
            {ic.total === 0
              ? 'No FF&E items yet'
              : `${ic.delivered + ic.ready_for_install + ic.installed} of ${ic.total} items ready or installed`}
          </div>
        </div>
      </div>

      {/* OVERALL TIMELINE */}
      <div className={panel} style={{
        background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(30,30,30,0.9) 50%, rgba(0,0,0,0.95) 100%)',
        boxShadow: '0 0 25px #D4A57430, inset 0 0 50px rgba(212, 165, 116, 0.08)',
      }}>
        <h3 className="text-2xl font-bold text-[#D4A574] mb-6">📊 Complete Project Timeline</h3>

        <div className="relative h-24 mb-8">
          <div className="absolute top-1/2 left-0 right-0 h-3 bg-gray-800 rounded-full transform -translate-y-1/2"
            style={{ boxShadow: 'inset 0 0 10px rgba(0,0,0,0.8)' }}></div>

          {/* Progress is the share of FF&E items already delivered or installed. */}
          <div className="absolute top-1/2 left-0 h-3 rounded-full transform -translate-y-1/2"
            data-testid="overall-progress"
            style={{
              width: `${installedPct}%`,
              background: 'linear-gradient(to right, #D4A574, #8B4513, #4A3A6A)',
              boxShadow: '0 0 15px #D4A57450',
            }}></div>

          <div className="absolute top-1/2 transform -translate-y-1/2" style={{ left: '0%' }}>
            <div className="w-5 h-5 bg-white rounded-full border-4 border-[#D4A574]" style={{ boxShadow: '0 0 10px #D4A574' }}></div>
            <div className="text-xs text-[#D4A574] mt-2 font-bold whitespace-nowrap">START</div>
            <div className="text-xs text-gray-400 whitespace-nowrap">Today</div>
          </div>

          <div className="absolute top-1/2 transform -translate-y-1/2" style={{ left: `${presLeft}%` }}>
            <div className="w-5 h-5 rounded-full border-4" style={{ background: '#8B4513', borderColor: '#D4A574', boxShadow: '0 0 10px #8B4513' }}></div>
            <div className="text-xs text-[#B49B7E] mt-2 font-bold whitespace-nowrap">PRESENTATION</div>
            <div className="text-xs text-gray-400 whitespace-nowrap">{daysToPresentation}d</div>
          </div>

          <div className="absolute top-1/2 transform -translate-y-1/2" style={{ left: `${instLeft}%` }}>
            <div className="w-5 h-5 rounded-full border-4" style={{ background: '#4A3A6A', borderColor: '#D4A574', boxShadow: '0 0 10px #4A3A6A' }}></div>
            <div className="text-xs text-[#D4C5A9] mt-2 font-bold whitespace-nowrap">INSTALL</div>
            <div className="text-xs text-gray-400 whitespace-nowrap">{daysToInstall}d</div>
          </div>
        </div>

        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          {[
            ['To Select', pc.to_be_selected, 'text-white'],
            ['In Progress', pc.in_progress, 'text-[#D4C5A9]'],
            ['Ready to Present', pc.ready, 'text-[#D4A574]'],
            ['Ordered', ic.ordered, 'text-[#B49B7E]'],
            ['In Transit', ic.in_transit, 'text-[#D4C5A9]'],
            ['Ready/Installed', ic.ready_for_install + ic.installed, 'text-[#D4A574]'],
          ].map(([label, value, colour]) => (
            <div key={label}>
              <div className="text-sm text-[#B49B7E] mb-2">{label}</div>
              <div className={`text-2xl font-bold ${colour}`}>{value}</div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-[#B49B7E] mt-4">
          The first three come from the Checklist, the last three from the FF&amp;E.
        </p>
      </div>

      {/* PRESENTATION PROGRESS (CHECKLIST) */}
      <div className={panel} style={{
        background: 'linear-gradient(135deg, #8B4513FF 0%, rgba(0,0,0,0.95) 30%, rgba(139,69,19,0.2) 70%, rgba(0,0,0,0.95) 100%)',
        boxShadow: '0 0 25px #8B451330, inset 0 0 50px rgba(139, 69, 19, 0.08)',
      }}>
        <h3 className="text-2xl font-bold text-[#D4A574] mb-6">🎨 Timeline to Presentation <span className="text-sm font-normal text-[#B49B7E]">— Checklist</span></h3>

        <div className="relative h-20 mb-6">
          <div className="absolute top-1/2 left-0 right-0 h-3 bg-gray-800 rounded-full transform -translate-y-1/2"
            style={{ boxShadow: 'inset 0 0 10px rgba(0,0,0,0.8)' }}></div>
          <div className="absolute top-1/2 left-0 h-3 rounded-full transform -translate-y-1/2"
            data-testid="presentation-progress"
            style={{
              width: `${presPct}%`,
              background: 'linear-gradient(to right, #B49B7E, #D4A574)',
              boxShadow: '0 0 15px #D4A57460',
            }}></div>

          <div className="absolute top-1/2 transform -translate-y-1/2" style={{ left: '0%' }}>
            <div className="w-4 h-4 bg-gray-600 rounded-full border-2 border-[#B49B7E]"></div>
            <div className="text-xs text-[#B49B7E] mt-2 whitespace-nowrap">TO BE SELECTED</div>
            <div className="text-xs text-white font-bold">{pc.to_be_selected}</div>
          </div>
          <div className="absolute top-1/2 transform -translate-y-1/2" style={{ left: '25%' }}>
            <div className="w-4 h-4 bg-[#8B4513] rounded-full border-2 border-[#D4A574]" style={{ boxShadow: '0 0 8px #8B4513' }}></div>
            <div className="text-xs text-[#B49B7E] mt-2 whitespace-nowrap">IN PROGRESS</div>
            <div className="text-xs text-white font-bold">{pc.in_progress}</div>
          </div>
          <div className="absolute top-1/2 transform -translate-y-1/2" style={{ left: '80%' }}>
            <div className="w-4 h-4 bg-[#D4A574] rounded-full border-2 border-white" style={{ boxShadow: '0 0 12px #D4A574' }}></div>
            <div className="text-xs text-[#D4A574] mt-2 font-bold whitespace-nowrap">READY FOR PRESENTATION</div>
            <div className="text-xs text-white font-bold">{pc.ready}</div>
          </div>
        </div>

        <div className="text-center text-[#D4A574] font-bold text-lg" data-testid="presentation-progress-label">
          {pc.total === 0
            ? 'Nothing on the Checklist yet'
            : `${pc.ready} of ${pc.total} items ready • ${presPct}% complete`}
        </div>
      </div>

      {/* INSTALL PROGRESS (FF&E) */}
      <div className={panel} style={{
        background: 'linear-gradient(135deg, #4A3A6AFF 0%, rgba(0,0,0,0.95) 30%, rgba(74,58,106,0.2) 70%, rgba(0,0,0,0.95) 100%)',
        boxShadow: '0 0 25px #4A3A6A30, inset 0 0 50px rgba(74, 58, 106, 0.08)',
      }}>
        <h3 className="text-2xl font-bold text-[#D4A574] mb-6">🏠 Timeline to Install <span className="text-sm font-normal text-[#B49B7E]">— FF&amp;E</span></h3>

        <div className="relative h-20 mb-6">
          <div className="absolute top-1/2 left-0 right-0 h-3 bg-gray-800 rounded-full transform -translate-y-1/2"
            style={{ boxShadow: 'inset 0 0 10px rgba(0,0,0,0.8)' }}></div>
          <div className="absolute top-1/2 left-0 h-3 rounded-full transform -translate-y-1/2"
            data-testid="install-progress"
            style={{
              width: `${installedPct}%`,
              background: 'linear-gradient(to right, #4A3A6A, #D4A574)',
              boxShadow: '0 0 15px #4A3A6A60',
            }}></div>

          {[['ORDERED', '0%', ic.ordered], ['IN TRANSIT', '35%', ic.in_transit],
            ['DELIVERED', '65%', ic.delivered], ['INSTALLED', '88%', ic.installed]].map(([label, left, value]) => (
            <div key={label} className="absolute top-1/2 transform -translate-y-1/2" style={{ left }}>
              <div className="w-4 h-4 bg-[#4A3A6A] rounded-full border-2 border-[#D4A574]"></div>
              <div className="text-xs text-[#B49B7E] mt-2 whitespace-nowrap">{label}</div>
              <div className="text-xs text-white font-bold">{value}</div>
            </div>
          ))}
        </div>

        <div className="text-center text-[#D4A574] font-bold text-lg" data-testid="install-progress-label">
          {ic.total === 0
            ? 'Nothing on the FF&E yet'
            : `${ic.delivered + ic.ready_for_install + ic.installed} of ${ic.total} items delivered or installed • ${installedPct}% complete`}
        </div>
      </div>

      {/* TWO CRITICAL PATHS */}
      <CriticalPathTable
        testId="critical-path-presentation"
        title="⚠️ Critical Path — Presentation"
        subtitle="What is holding up showing this to the client"
        source="Checklist"
        items={presBlockers}
        headerBg="linear-gradient(135deg, #8B4513FF 0%, #8B4513AA 50%, #8B4513FF 100%)"
      />

      <CriticalPathTable
        testId="critical-path-install"
        title="⚠️ Critical Path — Install"
        subtitle="What could delay the installation"
        source="FF&E"
        items={instBlockers}
        headerBg="linear-gradient(135deg, #8B4444FF 0%, #8B4444AA 50%, #8B4444FF 100%)"
      />

      <p className="text-xs text-[#B49B7E] flex items-center gap-2">
        <Clock className="w-3.5 h-3.5" />
        Dates are estimated from each item&apos;s status, order date and lead time. Fill in
        estimated delivery dates on the FF&amp;E to make the install date exact.
      </p>
    </div>
  );
};

export default InstallationCalendar;
